import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addDays, eachDayOfInterval } from 'date-fns';
import { Modal, Button, Checkbox } from '../common';
import DatePicker from '../common/DatePicker';
import { toast } from 'react-hot-toast';
import { shiftService } from '../../lib/api/services/shiftService';

// Define schema for bulk shift creation
const bulkShiftSchema = z.object({
  start_date: z.date({ required_error: 'Start date is required' }),
  end_date: z.date({ required_error: 'End date is required' })
    .refine(date => date instanceof Date, {
      message: 'End date is required',
    }),
  day_shifts: z.boolean().default(true),
  night_shifts: z.boolean().default(true),
});

type BulkShiftFormValues = z.infer<typeof bulkShiftSchema>;

interface BulkShiftCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: string;
  onShiftsCreated?: () => void;
}

const BulkShiftCreationModal: React.FC<BulkShiftCreationModalProps> = ({
  isOpen,
  onClose,
  scheduleId,
  onShiftsCreated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { control, handleSubmit, watch, formState: { errors }, reset } = useForm<BulkShiftFormValues>({
    resolver: zodResolver(bulkShiftSchema),
    defaultValues: {
      start_date: new Date(),
      end_date: addDays(new Date(), 6), // Default to a week
      day_shifts: true,
      night_shifts: true,
    }
  });
  
  const startDate = watch('start_date');
  const endDate = watch('end_date');
  const dayShifts = watch('day_shifts');
  const nightShifts = watch('night_shifts');
  
  const countShifts = () => {
    if (!startDate || !endDate) return 0;
    
    const daysCount = eachDayOfInterval({ start: startDate, end: endDate }).length;
    const shiftsPerDay = (dayShifts ? 1 : 0) + (nightShifts ? 1 : 0);
    
    return daysCount * shiftsPerDay;
  };
  
  const onSubmit = async (data: BulkShiftFormValues) => {
    if (!data.day_shifts && !data.night_shifts) {
      toast.error('Please select at least one shift type');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Generate shifts for each day in the interval
      const days = eachDayOfInterval({ start: data.start_date, end: data.end_date });
      
      const shifts = days.flatMap(day => {
        const formattedDate = format(day, 'yyyy-MM-dd');
        const shiftsForDay = [];
        
        if (data.day_shifts) {
          shiftsForDay.push({
            schedule_id: scheduleId,
            date: formattedDate,
            shift_type: 'day',
            provider_id: null,
          });
        }
        
        if (data.night_shifts) {
          shiftsForDay.push({
            schedule_id: scheduleId,
            date: formattedDate,
            shift_type: 'night',
            provider_id: null,
          });
        }
        
        return shiftsForDay;
      });
      
      // Use the shiftService instead of direct API call
      const { error } = await shiftService.createShifts(shifts);
      
      if (error) {
        throw error;
      }
      
      toast.success(`${shifts.length} shifts created successfully`);
      reset();
      onClose();
      
      if (onShiftsCreated) {
        onShiftsCreated();
      }
    } catch (error) {
      console.error('Error creating shifts:', error);
      toast.error('Failed to create shifts. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Multiple Shifts">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-4 space-y-4">
          <p className="text-gray-600 mb-4">
            This will create shifts for each day in the selected date range.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Controller
                name="start_date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Start Date"
                    selected={field.value}
                    onChange={field.onChange}
                    error={errors.start_date?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>
            
            <div>
              <Controller
                name="end_date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="End Date"
                    selected={field.value}
                    onChange={field.onChange}
                    error={errors.end_date?.message}
                    disabled={isSubmitting}
                    minDate={startDate}
                  />
                )}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="font-medium text-gray-700">Shift Types to Create</div>
            
            <div className="flex space-x-6">
              <Controller
                name="day_shifts"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="day-shifts-checkbox"
                    label="Day Shifts"
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                )}
              />
              
              <Controller
                name="night_shifts"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="night-shifts-checkbox"
                    label="Night Shifts"
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <p className="text-blue-700">
              This will create <strong>{countShifts()}</strong> shifts in total.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            disabled={countShifts() === 0}
          >
            Create Shifts
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BulkShiftCreationModal; 